import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const tr = (lang, pt, en, es) => (lang === 'pt' ? pt : lang === 'en' ? en : es);

export function OfflineBanner() {
  const { language } = useLanguage();
  const [online, setOnline] = useState(navigator.onLine);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'syncing' | 'restored'

  useEffect(() => {
    const goOffline = () => { setOnline(false); setPhase('idle'); };
    const goOnline = () => {
      setOnline(true);
      setPhase('syncing');
      // Brief "syncing" state lets the user see the transition.
      const t1 = setTimeout(() => setPhase('restored'), 900);
      const t2 = setTimeout(() => setPhase('idle'), 3500);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (online && phase === 'idle') return null;

  if (online && phase === 'syncing') {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-sky-600 text-white text-center py-1.5 text-xs font-medium flex items-center justify-center gap-2 animate-in slide-in-from-top" data-testid="syncing-banner">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        {tr(language, 'Sincronizando…', 'Syncing…', 'Sincronizando…')}
      </div>
    );
  }

  if (online && phase === 'restored') {
    return (
      <div className="fixed top-0 left-0 right-0 z-[100] bg-emerald-600 text-white text-center py-1.5 text-xs font-medium flex items-center justify-center gap-2 animate-in slide-in-from-top" data-testid="online-banner">
        <Wifi className="w-3.5 h-3.5" />
        {tr(language, 'Tudo atualizado ✓', 'All up to date ✓', 'Todo actualizado ✓')}
      </div>
    );
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-600 text-white text-center py-1.5 text-xs font-medium flex items-center justify-center gap-2 animate-in slide-in-from-top" data-testid="offline-banner">
      <WifiOff className="w-3.5 h-3.5" />
      {tr(language, 'Você está offline — exibindo dados salvos', 'You are offline — showing cached data', 'Estás sin conexión — mostrando datos guardados')}
    </div>
  );
}

export function OfflineFallback({ children, cacheName }) {
  const { language } = useLanguage();
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const goOffline = () => setOnline(false);
    const goOnline = () => setOnline(true);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  if (!online && !children) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-6 text-center" data-testid="offline-fallback">
        <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
          <WifiOff className="w-8 h-8 text-amber-600" />
        </div>
        <h2 className="text-lg font-bold text-gray-800 mb-2">
          {tr(language, 'Sem conexão', 'No connection', 'Sin conexión')}
        </h2>
        <p className="text-sm text-gray-500 max-w-xs">
          {cacheName
            ? tr(language,
                `Não há dados salvos de "${cacheName}" no seu dispositivo. Conecte-se à internet para carregar.`,
                `No cached data for "${cacheName}" on this device. Connect to the internet to load.`,
                `No hay datos guardados de "${cacheName}" en este dispositivo. Conéctate a internet para cargar.`)
            : tr(language,
                'Conecte-se à internet para acessar este conteúdo.',
                'Please connect to the internet to access this content.',
                'Conéctate a internet para acceder a este contenido.')}
        </p>
      </div>
    );
  }

  return children;
}
